import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../elements/Modal';
import Icon from './Icon';
import { apiRequest } from '../../utils';

/* The sheet behind everything the company has not got yet — a locked tile on the admin home, a
   locked "New event" button, a brand they have never booked. One line saying it is not part of
   their package, and "I'm interested", which mails and bells the brand's commercial people
   (POST /api/company/module-interest). Visible on purpose: it is how an admin sees what more there
   is, and the tap is the clearest buying signal the platform produces.

   Props:
     title    what they tapped (a module or a brand name); null keeps the sheet closed
     module   the paid module key, or
     brandId  the business unit, for a brand they have never booked
     text     the line above the button (defaults to "not part of your package yet")
     onClose  */
const ModuleInterest = ({ title, module, brandId, text, onClose }) => {
  const { t } = useTranslation();
  const [state, setState] = useState('idle');          // idle | busy | sent | failed
  if (!title) return null;
  const send = () => {
    if (state === 'busy') return;
    setState('busy');
    apiRequest('POST', '/company/module-interest',
      brandId ? { brand_bu_id: brandId } : { module })
      .then(() => setState('sent'))
      .catch(() => setState('failed'));
  };
  const close = () => { setState('idle'); onClose(); };
  return (
    <Modal show title={title} onClose={close} hideFooter>
      <p className="mb-3">
        {state === 'sent' ? t('moduleInterestSent') : (text || t('moduleNotInPackage'))}
      </p>
      {state === 'failed' && <p className="text-danger mb-3">{t('somethingWentWrong')}</p>}
      {state !== 'sent' && (
        <button type="button" className="bravos-submit" disabled={state === 'busy'} onClick={send}>
          <Icon name="hand-wave" /> {t('moduleImInterested')}
        </button>
      )}
    </Modal>
  );
};

/* "New event" / "New programme" for a company without the Own events module: the same button,
   locked, opening the sheet above. */
export const LockedCreateButton = ({ label }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="bravos-submit bravos-submit--ghost bravos-locked-create"
              onClick={() => setOpen(true)}>
        <Icon name="lock" /> {label}
      </button>
      <ModuleInterest title={open ? t('moduleOwnEvents') : null} module="company_events"
                      onClose={() => setOpen(false)} />
    </>
  );
};

/* Whether this company may BUILD on the free screens — the paid "Own events & programmes" module
   behind every create / add / edit button there (programmes, themes, houses, what counts, badges,
   the structure). Asked once per page load and shared: null while unknown, then true / false.
   The server refuses a write without the module either way; this only decides which button to draw. */
let canBuildRequest = null;
export const useCanBuild = () => {
  const [can, setCan] = useState(null);
  useEffect(() => {
    if (!canBuildRequest) {
      canBuildRequest = apiRequest('GET', '/company/modules')
        .then((r) => !!(r && r.can_build))
        .catch(() => true);
    }
    let live = true;
    canBuildRequest.then((v) => { if (live) setCan(v); });
    return () => { live = false; };
  }, []);
  return can;
};

export default ModuleInterest;
